// screens/TaxInfoScreen.js
// ============================================================================
// User-facing tax management — IRS 1099-MISC compliance flow.
//
// Two states:
//   1) No active tax document — show YTD context + "Submit Tax Form" CTA
//      that routes to a US/non-US choice and then to the Avalara hosted
//      portal (or simulates submission in mock mode).
//   2) Active tax document on file — show redacted summary + update option.
//
// Critically: the full SSN/TIN NEVER touches this screen. Collection happens
// on Avalara's hosted portal so CritCash never sees the sensitive data.
// We display only metadata (form type, last 4, submission date).
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Pressable,
  ActivityIndicator, ScrollView, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { httpsCallable, getFunctions } from 'firebase/functions';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';

import {
  THRESHOLD_CENTS,
  EARLY_WARNING_CENTS,
  ESCALATION_CENTS,
  TAX_DOC_STATUS_LABELS,
  formatThresholdDollars,
} from '../constants/taxConstants';

const COLORS = {
  bg: '#000000',
  card: '#111111',
  cardAlt: '#0a0a0a',
  border: '#1f1f1f',
  borderLight: '#2a2a2a',
  primary: '#FCD000',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  text: '#ffffff',
  textMuted: '#888888',
  textDim: '#555555',
};

const fmtCents = (cents) => {
  const n = Number(cents) || 0;
  return `$${(n / 100).toFixed(2)}`;
};

const fmtDate = (ms) => {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

export default function TaxInfoScreen({ navigation }) {
  const auth = getAuth();
  const functions = getFunctions();

  const [user, setUser] = useState(null);
  const [guardLoading, setGuardLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [statusText, setStatusText] = useState(null);
  const [data, setData] = useState(null);
  const [formTypeChoice, setFormTypeChoice] = useState(null); // 'w9' | 'w8ben' | null
  const [showFormChoice, setShowFormChoice] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null);
        setGuardLoading(false);
        navigation.replace('Landing');
        return;
      }
      setUser(u);
      setGuardLoading(false);
    });
    return unsub;
  }, [auth, navigation]);

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setErrorText(null);
    try {
      const fn = httpsCallable(functions, 'getMyTaxStatus');
      const result = await fn({});
      setData(result.data);
    } catch (e) {
      console.error('[tax-info] status fetch failed:', e);
      setErrorText(`Failed to load tax status: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [user, functions]);

  useEffect(() => {
    if (user) fetchStatus();
  }, [user, fetchStatus]);

  const submitTaxForm = async (formType) => {
    setSubmitting(true);
    setErrorText(null);
    setStatusText(null);
    try {
      const returnUrl = Platform.OS === 'web'
        ? `${window.location.origin}/tax-info`
        : 'https://critcash.com/tax-info';

      const fn = httpsCallable(functions, 'createTaxFormCollectionLink');
      const result = await fn({ formType, returnUrl });
      const { portalUrl, mockMode } = result.data || {};

      if (mockMode) {
        // Mock mode: backend already auto-completed the form. Just refresh status.
        setStatusText('Tax form submitted (mock mode — Avalara not configured yet).');
        setShowFormChoice(false);
        setFormTypeChoice(null);
        await fetchStatus();
      } else {
        // Real mode: open Avalara's hosted portal in a new tab. Avalara has
        // no webhook mechanism, so we'll poll syncMyTaxFormStatus (which
        // hits Avalara's GET form_requests endpoint) for the next ~2 minutes
        // until we see signed_at populated. Scheduled poller (every 15 min)
        // is the safety net if the user never returns.
        if (Platform.OS === 'web' && portalUrl) {
          window.open(portalUrl, '_blank', 'noopener,noreferrer');
        }
        setStatusText('Opening tax form portal in a new tab. Complete it there, then return here — the page will update automatically once your form is signed.');
        setShowFormChoice(false);
        setFormTypeChoice(null);

        // Poll syncMyTaxFormStatus for ~2 min (every 5s). Each tick asks the
        // backend to ping Avalara; if Avalara says "signed", the backend
        // updates Firestore and fetchStatus picks it up.
        const syncFn = httpsCallable(functions, 'syncMyTaxFormStatus');
        let attempts = 0;
        const poll = async () => {
          attempts++;
          try {
            const r = await syncFn();
            if (r.data?.synced) {
              await fetchStatus();
              return; // done
            }
          } catch (e) {
            console.warn('[tax-info] sync poll attempt failed:', e?.message || e);
          }
          // Always refresh display state regardless of sync result
          await fetchStatus();
          if (attempts < 24) setTimeout(poll, 5000);
        };
        setTimeout(poll, 5000);
      }
    } catch (e) {
      console.error('[tax-info] submit failed:', e);
      setErrorText(`Failed to start tax form submission: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (guardLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.center}>
          <ActivityIndicator color={COLORS.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <Header navigation={navigation} />
      <NavBar navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.titleRow}>
          <Ionicons name="document-text-outline" size={28} color={COLORS.primary} />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={styles.title}>Tax Info</Text>
            <Text style={styles.subtitle}>IRS 1099-NEC compliance for your CritCash earnings</Text>
          </View>
        </View>

        {errorText && (
          <View style={styles.errorBanner}>
            <Ionicons name="alert-circle" size={18} color={COLORS.danger} />
            <Text style={styles.errorText}>{errorText}</Text>
          </View>
        )}

        {statusText && !errorText && (
          <View style={styles.statusBanner}>
            <Ionicons name="checkmark-circle" size={18} color={COLORS.success} />
            <Text style={styles.statusBannerText}>{statusText}</Text>
          </View>
        )}

        {loading ? (
          <View style={styles.card}>
            <ActivityIndicator color={COLORS.primary} />
            <Text style={styles.dim}>Loading your tax status…</Text>
          </View>
        ) : data ? (
          <>
            {/* YTD earnings card */}
            <View style={styles.card}>
              <Text style={styles.cardEyebrow}>This year</Text>
              <Text style={styles.bigNumber}>{fmtCents(data.currentYear?.totalPrizeCents || 0)}</Text>
              <Text style={styles.dim}>
                in earnings from CritCash · {data.currentYear?.distributionCount || 0} credit{data.currentYear?.distributionCount === 1 ? '' : 's'}
              </Text>

              {/* Threshold progress bar */}
              {(() => {
                const total = data.currentYear?.totalPrizeCents || 0;
                const pct = Math.min(100, (total / THRESHOLD_CENTS) * 100);
                const tone = total >= THRESHOLD_CENTS
                  ? COLORS.warning
                  : total >= EARLY_WARNING_CENTS
                    ? COLORS.info
                    : COLORS.success;
                return (
                  <View style={{ marginTop: 12 }}>
                    <View style={styles.progressOuter}>
                      <View style={[styles.progressInner, { width: `${pct}%`, backgroundColor: tone }]} />
                    </View>
                    <View style={styles.progressLegend}>
                      <Text style={styles.progressLegendText}>$0</Text>
                      <Text style={styles.progressLegendText}>{formatThresholdDollars(EARLY_WARNING_CENTS)} heads-up</Text>
                      <Text style={styles.progressLegendText}>{formatThresholdDollars(THRESHOLD_CENTS)} IRS threshold</Text>
                    </View>
                  </View>
                );
              })()}
            </View>

            {/* Gate banner if user is currently blocked */}
            {data.withdrawalsGated && (
              <View style={styles.gateBanner}>
                <Ionicons name="lock-closed" size={18} color={COLORS.danger} />
                <Text style={styles.gateBannerText}>{data.gateReason}</Text>
              </View>
            )}

            {/* Active doc card or submit-CTA card */}
            {data.activeTaxDocument ? (
              <View style={styles.card}>
                <View style={styles.cardHeader}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.cardEyebrow}>Tax form on file</Text>
                    <Text style={styles.cardTitle}>
                      {data.activeTaxDocument.formType === 'w9' ? 'Form W-9 (U.S.)' : 'Form W-8BEN (non-U.S.)'}
                    </Text>
                  </View>
                  <View style={[
                    styles.statusChip,
                    { borderColor: TAX_DOC_STATUS_LABELS[data.activeTaxDocument.status]?.color || COLORS.textMuted },
                  ]}>
                    <Text style={[
                      styles.statusChipText,
                      { color: TAX_DOC_STATUS_LABELS[data.activeTaxDocument.status]?.color || COLORS.textMuted },
                    ]}>
                      {TAX_DOC_STATUS_LABELS[data.activeTaxDocument.status]?.displayName || data.activeTaxDocument.status}
                    </Text>
                  </View>
                </View>

                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Name on file</Text>
                  <Text style={styles.detailValue}>{data.activeTaxDocument.legalName || '—'}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Country</Text>
                  <Text style={styles.detailValue}>{data.activeTaxDocument.country || '—'}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>TIN ending in</Text>
                  <Text style={styles.detailValue}>
                    {data.activeTaxDocument.tinLast4 ? `****${data.activeTaxDocument.tinLast4}` : '—'}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Submitted</Text>
                  <Text style={styles.detailValue}>{fmtDate(data.activeTaxDocument.submittedAt)}</Text>
                </View>

                <Text style={[styles.dim, { marginTop: 12, fontSize: 12 }]}>
                  Your sensitive tax info is held by Avalara 1099 & W-9 (our IRS-authorized filing partner), not on CritCash's servers.
                  We see only the last 4 digits + your name and country.
                </Text>

                <Pressable
                  onPress={() => setShowFormChoice(true)}
                  style={({ pressed }) => [styles.secondaryBtn, pressed && { opacity: 0.85 }]}
                >
                  <Ionicons name="refresh" size={14} color={COLORS.text} />
                  <Text style={styles.secondaryBtnText}>Update Tax Form</Text>
                </Pressable>
              </View>
            ) : (
              <View style={styles.card}>
                <Text style={styles.cardEyebrow}>No tax form on file</Text>
                <Text style={styles.cardTitle}>Submit your W-9 or W-8BEN</Text>
                <Text style={[styles.dim, { marginTop: 8, lineHeight: 18 }]}>
                  We need a tax form from anyone who earns $600+ from CritCash in a calendar year.
                  Submitting early keeps your automatic payouts from being paused.{'\n\n'}
                  Your sensitive info is collected and stored by Avalara 1099 & W-9 (our IRS-authorized filing partner). CritCash never sees your full SSN/TIN.
                </Text>

                <Pressable
                  onPress={() => setShowFormChoice(true)}
                  style={({ pressed }) => [styles.primaryBtn, pressed && { opacity: 0.85 }]}
                  disabled={submitting}
                >
                  <Ionicons name="document-text" size={16} color={COLORS.bg} />
                  <Text style={styles.primaryBtnText}>Submit Tax Form</Text>
                </Pressable>
              </View>
            )}

            {/* Form-type choice card (US vs non-US) */}
            {showFormChoice && (
              <View style={styles.card}>
                <Text style={styles.cardTitle}>Are you a U.S. person for tax purposes?</Text>
                <Text style={[styles.dim, { marginTop: 6, marginBottom: 16, fontSize: 12, lineHeight: 16 }]}>
                  "U.S. person" includes U.S. citizens, U.S. residents (green card or substantial-presence test), and U.S. businesses (sole proprietors, LLCs, corporations).
                </Text>

                <Pressable
                  onPress={() => submitTaxForm('w9')}
                  disabled={submitting}
                  style={({ pressed }) => [styles.choiceBtn, pressed && !submitting && { opacity: 0.85 }, submitting && { opacity: 0.6 }]}
                >
                  {submitting && formTypeChoice === 'w9' ? (
                    <ActivityIndicator color={COLORS.bg} />
                  ) : (
                    <>
                      <Text style={styles.choiceBtnPrimary}>Yes — U.S. person</Text>
                      <Text style={styles.choiceBtnSecondary}>I'll submit a Form W-9</Text>
                    </>
                  )}
                </Pressable>

                <Pressable
                  onPress={() => submitTaxForm('w8ben')}
                  disabled={submitting}
                  style={({ pressed }) => [styles.choiceBtnAlt, pressed && !submitting && { opacity: 0.85 }, submitting && { opacity: 0.6 }]}
                >
                  {submitting && formTypeChoice === 'w8ben' ? (
                    <ActivityIndicator color={COLORS.text} />
                  ) : (
                    <>
                      <Text style={[styles.choiceBtnPrimary, { color: COLORS.text }]}>No — non-U.S.</Text>
                      <Text style={styles.choiceBtnSecondary}>I'll submit a Form W-8BEN</Text>
                    </>
                  )}
                </Pressable>

                <Pressable
                  onPress={() => setShowFormChoice(false)}
                  style={({ pressed }) => [styles.cancelBtn, pressed && { opacity: 0.85 }]}
                >
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </Pressable>
              </View>
            )}

            {/* Prior years section */}
            {data.priorYears && data.priorYears.length > 0 && (
              <View style={styles.card}>
                <Text style={styles.cardTitle}>Prior years</Text>
                {data.priorYears.map(py => (
                  <View key={py.year} style={styles.priorRow}>
                    <Text style={styles.priorYear}>{py.year}</Text>
                    <Text style={styles.priorAmount}>{fmtCents(py.totalPrizeCents)}</Text>
                  </View>
                ))}
                <Text style={[styles.dim, { marginTop: 8, fontSize: 11 }]}>
                  These years exceeded the $600 IRS threshold. A tax form is required for each.
                </Text>
              </View>
            )}
          </>
        ) : (
          <View style={styles.card}>
            <Text style={styles.dim}>No tax data available yet.</Text>
          </View>
        )}
      </ScrollView>
      <Footer navigation={navigation} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: COLORS.bg },
  scroll: { padding: 16, paddingBottom: 80 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.bg },
  dim: { color: COLORS.textMuted, fontSize: 13 },

  titleRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  title: { color: COLORS.text, fontSize: 22, fontWeight: '700' },
  subtitle: { color: COLORS.textMuted, fontSize: 13, marginTop: 2 },

  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: 'rgba(239,68,68,0.10)', borderColor: COLORS.danger, borderWidth: 1,
    borderRadius: 6, padding: 12, marginBottom: 12,
  },
  errorText: { color: COLORS.danger, fontSize: 13, flex: 1 },

  statusBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: 'rgba(34,197,94,0.08)', borderColor: COLORS.success, borderWidth: 1,
    borderRadius: 6, padding: 12, marginBottom: 12,
  },
  statusBannerText: { color: COLORS.success, fontSize: 13, flex: 1 },

  gateBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: 'rgba(239,68,68,0.15)', borderColor: COLORS.danger, borderWidth: 2,
    borderRadius: 8, padding: 14, marginBottom: 12,
  },
  gateBannerText: { color: COLORS.danger, fontSize: 13, fontWeight: '600', flex: 1 },

  card: {
    backgroundColor: COLORS.card, borderColor: COLORS.border, borderWidth: 1,
    borderRadius: 8, padding: 16, marginBottom: 12,
  },
  cardHeader: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 },
  cardEyebrow: { color: COLORS.textMuted, fontSize: 11, fontWeight: '600', letterSpacing: 0.5, textTransform: 'uppercase' },
  cardTitle: { color: COLORS.text, fontSize: 18, fontWeight: '700', marginTop: 4 },

  bigNumber: { color: COLORS.primary, fontSize: 36, fontWeight: '700', marginTop: 4, fontVariant: ['tabular-nums'] },

  progressOuter: {
    height: 8, backgroundColor: '#1a1a1a', borderRadius: 4, overflow: 'hidden',
  },
  progressInner: { height: '100%', borderRadius: 4 },
  progressLegend: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 },
  progressLegendText: { color: COLORS.textDim, fontSize: 10 },

  statusChip: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, borderWidth: 1 },
  statusChipText: { fontSize: 11, fontWeight: '600' },

  detailRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, borderBottomColor: COLORS.border, borderBottomWidth: 1 },
  detailLabel: { color: COLORS.textMuted, fontSize: 13 },
  detailValue: { color: COLORS.text, fontSize: 13, fontWeight: '600' },

  primaryBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    backgroundColor: COLORS.primary, paddingVertical: 12, paddingHorizontal: 18,
    borderRadius: 6, marginTop: 16,
  },
  primaryBtnText: { color: COLORS.bg, fontSize: 14, fontWeight: '700' },

  secondaryBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    backgroundColor: COLORS.cardAlt, borderColor: COLORS.border, borderWidth: 1,
    paddingVertical: 10, paddingHorizontal: 18, borderRadius: 6, marginTop: 16,
  },
  secondaryBtnText: { color: COLORS.text, fontSize: 13, fontWeight: '600' },

  choiceBtn: {
    backgroundColor: COLORS.primary, padding: 14, borderRadius: 8, alignItems: 'center', marginBottom: 10,
  },
  choiceBtnAlt: {
    backgroundColor: COLORS.cardAlt, borderColor: COLORS.borderLight, borderWidth: 1,
    padding: 14, borderRadius: 8, alignItems: 'center', marginBottom: 10,
  },
  choiceBtnPrimary: { color: COLORS.bg, fontSize: 16, fontWeight: '700' },
  choiceBtnSecondary: { color: COLORS.bg, fontSize: 12, marginTop: 4, opacity: 0.8 },

  cancelBtn: { padding: 10, alignItems: 'center' },
  cancelBtnText: { color: COLORS.textMuted, fontSize: 13 },

  priorRow: {
    flexDirection: 'row', justifyContent: 'space-between',
    paddingVertical: 8, borderBottomColor: COLORS.border, borderBottomWidth: 1,
  },
  priorYear: { color: COLORS.text, fontSize: 14, fontWeight: '600' },
  priorAmount: { color: COLORS.warning, fontSize: 14, fontWeight: '700', fontVariant: ['tabular-nums'] },
});
